import axios from "axios";

/**
 * Backend-URL ableiten:
 * - Lokale Entwicklung (localhost) und Preview-Umgebung: aus REACT_APP_BACKEND_URL (gleicher Host)
 * - Produktion/Custom-Domain: aus dem aktuellen window.location.origin
 *   (so funktionieren sowohl property-hub-839.emergent.host als auch domivus.de
 *    OHNE dass der Frontend-Build pro Domain neu gebaut werden muss).
 */
function resolveBackendUrl() {
  if (typeof window === "undefined") return process.env.REACT_APP_BACKEND_URL;
  const host = window.location.hostname;
  const isLocal = host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host);
  const isPreview = host.endsWith(".emergentagent.com");
  if (isLocal || isPreview) {
    return process.env.REACT_APP_BACKEND_URL || window.location.origin;
  }
  // Produktion: API liegt auf der gleichen Domain
  return window.location.origin;
}

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "domivus_access_token";
const REFRESH_KEY = "domivus_refresh_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);
export const setTokens = (access, refresh) => {
  if (access) localStorage.setItem(TOKEN_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
};
export const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
};

const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  // FormData nie als JSON senden (Default-Header) – Browser setzt multipart/form-data inkl. Boundary selbst
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    if (typeof config.headers?.setContentType === "function") config.headers.setContentType(null);
    else delete config.headers["Content-Type"];
  }
  const token = getToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshing = null;

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      !original._retry &&
      !original.url?.includes("/auth/login") &&
      !original.url?.includes("/auth/refresh") &&
      getRefreshToken()
    ) {
      original._retry = true;
      try {
        if (!refreshing) {
          refreshing = axios.post(`${API}/auth/refresh`, {
            refresh_token: getRefreshToken(),
          });
        }
        const { data } = await refreshing;
        refreshing = null;
        setTokens(data.access_token, data.refresh_token);
        original.headers.Authorization = `Bearer ${data.access_token}`;
        return api(original);
      } catch (_) {
        refreshing = null;
        clearTokens();
      }
    }
    // iter155 — Whitescreen-Prävention:
    // FastAPI/Pydantic liefern bei 422 ein `detail` als **Liste von Objekten**
    // ({type, loc, msg, input, url}). Wenn Code irgendwo `err.response.data.detail`
    // direkt in JSX rendert oder in einen State-String setzt, wirft React
    // Fehler #31 („Objects are not valid as a React child") und die ganze
    // Seite geht weiß. Wir normalisieren `detail` hier zentral zu einem
    // lesbaren String — das Original bleibt unter `_detail_raw` erhalten.
    if (error?.response?.data) {
      const d = error.response.data.detail;
      if (d != null && typeof d !== "string") {
        let flat;
        if (Array.isArray(d)) {
          flat = d.map((e) => {
            if (!e || typeof e !== "object") return String(e);
            const loc = Array.isArray(e.loc) ? e.loc.filter((x) => x !== "body").join(".") : "";
            return e.msg ? (loc ? `${loc}: ${e.msg}` : e.msg) : JSON.stringify(e);
          }).join(" · ");
        } else if (typeof d === "object") {
          try { flat = d.msg || d.message || JSON.stringify(d); }
          catch { flat = String(d); }
        } else {
          flat = String(d);
        }
        error.response.data._detail_raw = d;
        error.response.data.detail = flat;
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Unbekannter Fehler";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

// iter159w — Wrapper um `window.fetch`, der bei 401 transparent ein Refresh
// versucht und den Original-Call wiederholt. Nutzen alle Stellen, die ein
// PDF/Blob per fetch holen (Amtliche Dokumente, Eigentümer-Mappe, Reports,
// Logo, Migrations-Bundle, …). Verhindert das nervige „Token abgelaufen"
// nach längerer Session.
export async function authFetch(url, options = {}) {
  const doFetch = (tok) => window.fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${tok}`,
    },
  });
  let res = await doFetch(getToken());
  if (res.status !== 401 || !getRefreshToken()) return res;
  // Refresh & retry
  try {
    if (!refreshing) {
      refreshing = axios.post(`${API}/auth/refresh`, { refresh_token: getRefreshToken() });
    }
    const { data } = await refreshing;
    refreshing = null;
    setTokens(data.access_token, data.refresh_token);
    res = await doFetch(data.access_token);
  } catch (_) {
    refreshing = null;
    clearTokens();
  }
  return res;
}

export default api;
